import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Trophy } from "lucide-react";
import { useTrack, useTrackLeaderboard } from "@/hooks/useTracks";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatLapTime } from "@/utils/timeFormat";

export default function TrackLeaderboardPage() {
  const { t, i18n } = useTranslation(["pages", "common"]);
  const { trackId = "" } = useParams<{ trackId: string }>();
  const { data: track } = useTrack(trackId);
  const { data: entries = [], isLoading, error } = useTrackLeaderboard(trackId);
  usePageMeta({
    title: track ? t("titles.leaderboardAt", { name: track.name }) : undefined,
    description: track?.description ?? undefined,
  });

  if (error) {
    return (
      <div className="page-container">
        <div className="mb-8">
          <Link
            to="/tracks"
            className="inline-flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-600 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("pages:leaderboard.backToTracks")}
          </Link>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">
            {t("pages:leaderboard.errorLoading")}
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="mb-8">
        <Link
          to="/tracks"
          className="inline-flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-600 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("pages:leaderboard.backToTracks")}
        </Link>
        <div className="flex items-center gap-3 mt-3">
          <Trophy className="h-8 w-8 text-amber-500" />
          <div>
            <h1 className="page-title">
              {track ? track.name : t("pages:leaderboard.title")}
            </h1>
            <p className="page-subtitle">{t("pages:leaderboard.subtitle")}</p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg border border-secondary-200 p-8 animate-pulse">
          <div className="h-6 bg-secondary-200 rounded w-1/2 mb-4"></div>
          <div className="h-6 bg-secondary-200 rounded w-2/3"></div>
        </div>
      ) : entries.length === 0 ? (
        <div className="bg-white rounded-lg border border-secondary-200 p-8 text-center">
          <p className="text-secondary-600">{t("pages:leaderboard.empty")}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-secondary-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-secondary-100 text-left text-secondary-500">
                <th scope="col" className="px-4 py-3 font-medium">
                  {t("pages:leaderboard.table.rank")}
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  {t("pages:leaderboard.table.driver")}
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  {t("pages:leaderboard.table.lapTime")}
                </th>
                <th scope="col" className="px-4 py-3 font-medium hidden md:table-cell">
                  {t("pages:leaderboard.table.sectors")}
                </th>
                <th scope="col" className="px-4 py-3 font-medium hidden sm:table-cell">
                  {t("pages:leaderboard.table.vehicle")}
                </th>
                <th scope="col" className="px-4 py-3 font-medium hidden lg:table-cell">
                  {t("pages:leaderboard.table.date")}
                </th>
                <th scope="col" className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.sessionId}
                  className="border-b border-secondary-50 last:border-0"
                >
                  <td className="px-4 py-3 font-semibold text-secondary-900">
                    {entry.rank}
                  </td>
                  <td className="px-4 py-3 text-secondary-900">
                    {entry.driverName}
                  </td>
                  <td className="px-4 py-3 font-mono text-secondary-900">
                    {formatLapTime(entry.lapTime)}
                  </td>
                  <td className="px-4 py-3 font-mono text-secondary-500 hidden md:table-cell">
                    {entry.sectorTimes.map((sector, index) => (
                      <span key={index}>
                        {index > 0 && " / "}
                        {formatLapTime(sector)}
                      </span>
                    ))}
                  </td>
                  <td className="px-4 py-3 text-secondary-700 hidden sm:table-cell">
                    {entry.vehicle.year} {entry.vehicle.maker}{" "}
                    {entry.vehicle.model}
                  </td>
                  <td className="px-4 py-3 text-secondary-500 hidden lg:table-cell">
                    {new Date(entry.conductedAt).toLocaleDateString(i18n.language)}
                  </td>
                  <td className="px-4 py-3">
                    {entry.contentPublic && (
                      <Link
                        to={`/watch/${entry.sessionId}`}
                        className="text-primary-600 hover:text-primary-700 hover:underline"
                      >
                        {t("pages:leaderboard.watch")}
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
